// A tiny history router: the app has a dozen routes, so no dependency.
import { useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent } from "react";

const subs = new Set<() => void>();
const notify = () => subs.forEach((f) => f());
// guarded so the pure parts (match, redirects) load in node tests
if (typeof window !== "undefined") window.addEventListener("popstate", notify);

export function navigate(to: string, opts: { replace?: boolean } = {}) {
  const url = new URL(to, location.origin);
  if (url.origin !== location.origin) {
    location.href = url.href;
    return;
  }
  const next = url.pathname + url.search + url.hash;
  if (next === location.pathname + location.search + location.hash) return;
  if (opts.replace) history.replaceState(null, "", next);
  else history.pushState(null, "", next);
  window.scrollTo({ top: 0 });
  notify();
}

export function useLocation() {
  const href = useSyncExternalStore(
    (f) => {
      subs.add(f);
      return () => subs.delete(f);
    },
    () => location.pathname + location.search,
  );
  const url = new URL(href, location.origin);
  return { path: url.pathname.replace(/\/+$/, "") || "/", query: url.searchParams };
}

export type Route =
  | { name: "home" }
  | { name: "login" }
  | { name: "invite"; code: string }
  | { name: "project"; id: string }
  /** v4 (T4): review a finished version (?v=2&t=12.4&note=…) */
  | { name: "review"; id: string }
  // v4 (V4-H2, spec-v4 5.1): channel > collection > video
  | { name: "channel"; channelId: string }
  | { name: "channelSettings"; channelId: string }
  | { name: "collection"; id: string }
  | { name: "character"; characterId: string; collectionId?: string; projectId?: string }
  | { name: "library" }
  /** v3's Brand page: redirects to the main channel's settings (brandRedirect) */
  | { name: "brand" }
  | { name: "credits" }
  | { name: "settings" }
  | { name: "models" }
  | { name: "invites" }
  | { name: "people" }
  /** 2026-11-02: one account's spend (admins) */
  | { name: "personSpend"; id: string }
  | { name: "costs" }
  | { name: "services" }
  /** v4 (D7): problems, feedback and unmet asks sent through the Director */
  | { name: "feedback" }
  /** v4 (V4-SH): reports from share pages */
  | { name: "reports" }
  /** MS-UI: the model showcase job (admins) */
  | { name: "showcase" }
  /** Wave 1 safety: the safety queue and consent log (admins) */
  | { name: "safety" }
  | { name: "missing" };

export function match(path: string): Route {
  let m: RegExpMatchArray | null;
  if (path === "/") return { name: "home" };
  if (path === "/login") return { name: "login" };
  if ((m = path.match(/^\/invite\/([^/]+)$/))) return { name: "invite", code: decodeURIComponent(m[1]) };
  if ((m = path.match(/^\/p\/([^/]+)$/))) return { name: "project", id: decodeURIComponent(m[1]) };
  if ((m = path.match(/^\/p\/([^/]+)\/review$/))) return { name: "review", id: decodeURIComponent(m[1]) };
  if ((m = path.match(/^\/p\/([^/]+)\/cast\/([^/]+)$/))) return { name: "character", projectId: decodeURIComponent(m[1]), characterId: decodeURIComponent(m[2]) };
  if ((m = path.match(/^\/c\/([^/]+)$/))) return { name: "channel", channelId: decodeURIComponent(m[1]) };
  if ((m = path.match(/^\/c\/([^/]+)\/settings$/))) return { name: "channelSettings", channelId: decodeURIComponent(m[1]) };
  if ((m = path.match(/^\/k\/([^/]+)$/))) return { name: "collection", id: decodeURIComponent(m[1]) };
  if ((m = path.match(/^\/k\/([^/]+)\/cast\/([^/]+)$/))) return { name: "character", collectionId: decodeURIComponent(m[1]), characterId: decodeURIComponent(m[2]) };
  if (path === "/library") return { name: "library" };
  if (path === "/brand") return { name: "brand" };
  if (path === "/credits") return { name: "credits" };
  if (path === "/settings") return { name: "settings" };
  if (path === "/models") return { name: "models" };
  if (path === "/admin/invites") return { name: "invites" };
  if (path === "/admin/people") return { name: "people" };
  if ((m = path.match(/^\/admin\/people\/([^/]+)$/))) return { name: "personSpend", id: decodeURIComponent(m[1]!) };
  if (path === "/admin/costs") return { name: "costs" };
  if (path === "/admin/services") return { name: "services" };
  if (path === "/admin/feedback") return { name: "feedback" };
  if (path === "/admin/reports") return { name: "reports" };
  if (path === "/admin/showcase") return { name: "showcase" };
  if (path === "/admin/safety") return { name: "safety" };
  return { name: "missing" };
}

/** A channel's settings ("new": the screen that creates one). */
export const channelSettingsPath = (channelId: string | null | undefined) => `/c/${channelId ? encodeURIComponent(channelId) : "new"}/settings`;

/** `/brand` (v3's Brand page, old links and the Director's navigate "brand"): the main channel's settings. The main
 *  channel is the first one made (GET /channels is oldest first); with none, the screen that creates one. */
export function brandRedirect(channels: { id: string }[], query = ""): string {
  return channelSettingsPath(channels[0]?.id) + (query ? `?${query.replace(/^\?/, "")}` : "");
}

/** The tab a path belongs to: Home holds the channel, its collections and videos; Channel holds the settings. */
export function navArea(path: string): "home" | "library" | "channel" | "credits" | "other" {
  if (path === "/brand" || /^\/c\/[^/]+\/settings$/.test(path)) return "channel";
  if (path === "/" || path.startsWith("/p/") || path.startsWith("/k/") || /^\/c\/[^/]+$/.test(path)) return "home";
  if (path.startsWith("/library")) return "library";
  if (path.startsWith("/credits")) return "credits";
  return "other";
}

/** The channel a path names (/c/:id, /c/:id/settings), if any. */
export function channelInPath(path: string): string | null {
  const m = path.match(/^\/c\/([^/]+)(?:\/settings)?$/);
  return m && m[1] !== "new" ? decodeURIComponent(m[1]) : null;
}

export function Link({ to, onClick, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }) {
  return (
    <a
      href={to}
      {...rest}
      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(e);
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        navigate(to);
      }}
    />
  );
}
